import axios from 'axios';
import type {
  OrderCreate,
  OrderUpdate,
  OrderStatusUpdate,
  OrderSearchParams,
} from './types/order';
import type {
  ShipmentBookingData,
  RateRequest,
  CancellationRequest,
  ShipmentSearchParams,
} from './types/logistics';

// Create axios instance with proper configuration
const getBaseURL = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For production, use same host with port 8000
  if (window.location.hostname !== 'localhost') {
    return `http://${window.location.hostname}:8000`;
  }
  
  // For local development
  return 'http://localhost:8000';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api;

// Order Management API Functions

export const orderAPI = {
  // Get order status counts (single efficient query)
  getOrderStats: () =>
    api.get('/orders/stats'),

  // List orders with filtering and pagination
  listOrders: (params?: OrderSearchParams) => 
    api.get('/orders/', { params }),

  // Get single order by ID
  getOrder: (orderId: number) => 
    api.get(`/orders/${orderId}`),

  // Create new order
  createOrder: (orderData: OrderCreate) => 
    api.post('/orders/', orderData),

  // Update order
  updateOrder: (orderId: number, orderData: OrderUpdate) => 
    api.put(`/orders/${orderId}`, orderData),

  // Delete order
  deleteOrder: (orderId: number) => 
    api.delete(`/orders/${orderId}`),

  // Update order status
  updateOrderStatus: (orderId: number, statusUpdate: OrderStatusUpdate) => 
    api.put(`/orders/${orderId}/status`, statusUpdate),

  // Bulk update order status
  bulkUpdateStatus: (orderIds: number[], status: string, notes?: string) =>
    api.post('/orders/bulk-status', { order_ids: orderIds, status, notes }),

  // Bulk generate delivery challans
  bulkGenerateDC: (orderIds: number[], options: { warehouse_id?: number; vehicle_number?: string; transport_mode?: string; notes?: string }) =>
    api.post('/orders/bulk-generate-dc', { order_ids: orderIds, ...options }),

  // Deallocate a single order (returns to confirmed, restores inventory)
  deallocateOrder: (orderId: number) =>
    api.post(`/orders/${orderId}/deallocate`),

  // Bulk deallocate orders
  bulkDeallocate: (orderIds: number[], notes?: string) =>
    api.post('/orders/bulk-deallocate', { order_ids: orderIds, notes }),

  // Bulk delete orders (only pending/confirmed)
  bulkDelete: (orderIds: number[]) =>
    api.post('/orders/bulk-delete', { order_ids: orderIds }),

  // Put a single order on hold / release it from hold
  setHold: (orderId: number, onHold: boolean, notes?: string) =>
    api.put(`/orders/${orderId}/hold`, { on_hold: onHold, notes }),

  // Bulk put orders on hold / release from hold
  bulkHold: (orderIds: number[], onHold: boolean, notes?: string) =>
    api.post('/orders/bulk-hold', { order_ids: orderIds, on_hold: onHold, notes }),

  // Get order status history
  getOrderStatusHistory: (orderId: number) => 
    api.get(`/orders/${orderId}/status-history`),

  // Import orders from file
  importOrders: (file: File, format: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/orders/import?format=${format}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Export orders
  exportOrders: (format: 'csv' | 'excel', params?: Omit<OrderSearchParams, 'skip' | 'limit' | 'sort_by' | 'sort_order'>) => 
    api.get('/orders/export', { 
      params: { ...params, format },
      responseType: 'blob'
    }),
};

// Taxonomy API Functions

export const taxonomyAPI = {
  // Get all departments
  getDepartments: (activeOnly: boolean = false) =>
    api.get('/api/taxonomy/departments', { params: { active_only: activeOnly } }),

  // Get categories by department
  getCategories: (departmentId?: number, activeOnly: boolean = false) =>
    api.get('/api/taxonomy/categories', { 
      params: { 
        department_id: departmentId,
        active_only: activeOnly 
      } 
    }),

  // Get subcategories by category
  getSubcategories: (categoryId?: number, activeOnly: boolean = false) =>
    api.get('/api/taxonomy/subcategories', { 
      params: { 
        category_id: categoryId,
        active_only: activeOnly 
      } 
    }),

  // Search taxonomy
  searchTaxonomy: (query: string, level?: 'department' | 'category' | 'subcategory') =>
    api.get('/api/taxonomy/search', { 
      params: { 
        q: query,
        level: level 
      } 
    }),

  // Get full taxonomy path for a subcategory
  getTaxonomyPath: (subcategoryId: number) =>
    api.get(`/api/taxonomy/path/${subcategoryId}`),

  // Get complete taxonomy hierarchy
  getTaxonomyHierarchy: () =>
    api.get('/api/taxonomy/hierarchy'),

  // Department CRUD operations
  createDepartment: (departmentData: {
    name: string;
    code: string;
    abbreviation: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) =>
    api.post('/api/taxonomy/departments', departmentData),

  updateDepartment: (departmentId: number, departmentData: {
    name?: string;
    code?: string;
    abbreviation?: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) =>
    api.put(`/api/taxonomy/departments/${departmentId}`, departmentData),

  deleteDepartment: (departmentId: number) =>
    api.delete(`/api/taxonomy/departments/${departmentId}`),

  // Category CRUD operations
  createCategory: (categoryData: {
    name: string;
    department_id: number;
    abbreviation: string;
    code?: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) =>
    api.post('/api/taxonomy/categories', categoryData),

  updateCategory: (categoryId: number, categoryData: {
    name?: string;
    department_id?: number;
    abbreviation?: string;
    code?: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) =>
    api.put(`/api/taxonomy/categories/${categoryId}`, categoryData),

  deleteCategory: (categoryId: number) =>
    api.delete(`/api/taxonomy/categories/${categoryId}`),

  // Subcategory CRUD operations
  createSubcategory: (subcategoryData: {
    name: string;
    category_id: number;
    abbreviation: string;
    code?: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) =>
    api.post('/api/taxonomy/subcategories', subcategoryData),

  updateSubcategory: (subcategoryId: number, subcategoryData: {
    name?: string;
    category_id?: number;
    abbreviation?: string;
    code?: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) =>
    api.put(`/api/taxonomy/subcategories/${subcategoryId}`, subcategoryData),

  deleteSubcategory: (subcategoryId: number) =>
    api.delete(`/api/taxonomy/subcategories/${subcategoryId}`),
};

// Migration API Functions

export const migrationAPI = {
  // Load taxonomy from JSON
  loadTaxonomy: (jsonData: any) =>
    api.post('/api/migration/load-taxonomy', jsonData),

  // Trigger product migration
  triggerMigration: () =>
    api.post('/api/migration/migrate-products'),

  // Get migration status
  getMigrationStatus: () =>
    api.get('/api/migration/status'),

  // Validate migration results
  validateMigration: () =>
    api.post('/api/migration/validate'),
};

// Product Relationships API Functions

export interface ProductRelationship {
  id: number;
  product_id: number;
  related_product_id: number;
  relationship_type: string;
  related_product_name?: string;
  related_product_sku?: string;
  created_at?: string;
}

export const relationshipAPI = {
  // List all relationships for a product
  listRelationships: (productId: number) =>
    api.get<ProductRelationship[]>(`/products/${productId}/relationships`),

  // Create a new relationship
  createRelationship: (productId: number, relatedProductId: number, relationshipType: string) =>
    api.post<ProductRelationship>(`/products/${productId}/relationships`, {
      related_product_id: relatedProductId,
      relationship_type: relationshipType,
    }),

  // Delete a relationship
  deleteRelationship: (productId: number, relationshipId: number) =>
    api.delete(`/products/${productId}/relationships/${relationshipId}`),

  // Search products for autocomplete (uses existing product list endpoint)
  searchProducts: (query: string) =>
    api.get('/products/', {
      params: {
        search_field: 'name',
        search_operator: 'contains',
        search_value: query,
        limit: 10,
      },
    }),
};

// Channel Management API Functions

export interface Channel {
  id: number;
  name: string;
  code?: string;
  type: string;
  config?: Record<string, any>;
  order_template_config?: Record<string, any>;
  is_active: boolean;
  created_at?: string;
}

export const channelAPI = {
  listChannels: () =>
    api.get<Channel[]>('/channels/'),

  createChannel: (data: { name: string; code: string; type: string; config?: Record<string, any>; is_active?: boolean }) =>
    api.post<Channel>('/channels/', data),

  updateChannel: (channelId: number, data: { name?: string; code?: string; type?: string; config?: Record<string, any>; order_template_config?: Record<string, any>; is_active?: boolean }) =>
    api.put<Channel>(`/channels/${channelId}`, data),

  deleteChannel: (channelId: number) =>
    api.delete(`/channels/${channelId}`),

  // Product-Channel publishing
  publishProduct: (productId: number, data: { channel_id: number; channel_sku?: string; channel_price?: number; channel_description?: string }) =>
    api.post(`/products/${productId}/channels`, data),

  listProductChannels: (productId: number) =>
    api.get(`/products/${productId}/channels`),

  unpublishProduct: (productId: number, channelId: number) =>
    api.delete(`/products/${productId}/channels/${channelId}`),
};

// Logistics Management API Functions

export const logisticsAPI = {
  // Book a new shipment with the selected logistics provider
  // Converts camelCase frontend fields to snake_case for backend Pydantic schemas
  // Converts snake_case response back to camelCase for frontend consumption
  bookShipment: (shipmentData: ShipmentBookingData) =>
    api.post('/api/logistics/shipments/book', {
      provider: shipmentData.provider,
      sender_name: shipmentData.senderName,
      sender_address: shipmentData.senderAddress,
      sender_city: shipmentData.senderCity,
      sender_state: shipmentData.senderState,
      sender_pincode: shipmentData.senderPincode,
      sender_phone: shipmentData.senderPhone,
      recipient_name: shipmentData.recipientName,
      recipient_address: shipmentData.recipientAddress,
      recipient_city: shipmentData.recipientCity,
      recipient_state: shipmentData.recipientState,
      recipient_pincode: shipmentData.recipientPincode,
      recipient_phone: shipmentData.recipientPhone,
      weight: shipmentData.weight,
      length: shipmentData.length,
      width: shipmentData.width,
      height: shipmentData.height,
      declared_value: shipmentData.declaredValue ?? null,
    }).then(res => ({
      ...res,
      data: {
        id: res.data.id,
        awb: res.data.awb,
        provider: res.data.provider,
        status: res.data.status,
        createdAt: res.data.created_at,
        senderName: res.data.sender_name,
        recipientName: res.data.recipient_name,
        recipientCity: res.data.recipient_city,
        weight: res.data.weight,
      }
    })),

  // Track a shipment by AWB number
  // Converts snake_case response to camelCase
  trackShipment: (awb: string) =>
    api.get(`/api/logistics/shipments/${awb}/track`).then(res => ({
      ...res,
      data: {
        awb: res.data.awb,
        currentStatus: res.data.current_status,
        provider: res.data.provider,
        events: (res.data.events || []).map((e: any) => ({
          status: e.status,
          location: e.location,
          remarks: e.remarks,
          timestamp: e.timestamp,
        })),
        estimatedDelivery: res.data.estimated_delivery,
      }
    })),

  // Calculate shipping rates from all enabled providers
  // Converts camelCase frontend fields to snake_case for backend Pydantic schemas
  // Converts snake_case response back to camelCase for frontend consumption
  calculateRates: (rateRequest: RateRequest) =>
    api.post('/api/logistics/shipments/rates', {
      origin_pincode: rateRequest.originPincode,
      destination_pincode: rateRequest.destinationPincode,
      weight: rateRequest.weight,
      length: rateRequest.length,
      width: rateRequest.width,
      height: rateRequest.height,
      declared_value: rateRequest.declaredValue ?? null,
    }).then(res => ({
      ...res,
      data: (res.data || []).map((r: any) => ({
        provider: r.provider,
        rate: r.rate,
        currency: r.currency,
        estimatedDeliveryDays: r.estimated_delivery_days,
        serviceType: r.service_type,
      }))
    })),

  // Cancel a shipment by AWB number
  // Converts snake_case response to camelCase
  cancelShipment: (awb: string, cancellationRequest: CancellationRequest) =>
    api.post(`/api/logistics/shipments/${awb}/cancel`, cancellationRequest).then(res => ({
      ...res,
      data: {
        awb: res.data.awb,
        status: res.data.status,
        message: res.data.message,
        cancelledAt: res.data.cancelled_at,
      }
    })),

  // Get shipments with filtering and pagination
  // Converts snake_case response to camelCase
  getShipments: (params?: ShipmentSearchParams) =>
    api.get('/api/logistics/shipments', { params }).then(res => ({
      ...res,
      data: {
        ...res.data,
        shipments: (res.data.shipments || []).map((s: any) => ({
          id: s.id,
          awb: s.awb,
          provider: s.provider,
          status: s.status,
          recipientName: s.recipient_name,
          recipientCity: s.recipient_city,
          createdAt: s.created_at,
          weight: s.weight,
        }))
      }
    })),

  // Export shipments to CSV format
  exportShipments: (params?: Omit<ShipmentSearchParams, 'skip' | 'limit'>) =>
    api.get('/api/logistics/shipments/export/csv', {
      params,
      responseType: 'blob'
    }),

  // Admin endpoints for provider configuration (admin role required)
  admin: {
    // Get all provider configurations
    getProviderConfigurations: () =>
      api.get('/api/logistics/admin/providers'),

    // Create new provider configuration
    createProviderConfiguration: (configData: {
      providerName: string;
      apiKey: string;
      apiSecret: string;
      baseUrl: string;
      webhookSecret?: string;
      configJson?: Record<string, any>;
    }) =>
      api.post('/api/logistics/admin/providers', configData),

    // Update provider configuration
    updateProviderConfiguration: (providerId: number, configData: {
      apiKey?: string;
      apiSecret?: string;
      baseUrl?: string;
      isEnabled?: boolean;
      webhookSecret?: string;
      configJson?: Record<string, any>;
    }) =>
      api.put(`/api/logistics/admin/providers/${providerId}`, configData),

    // Delete provider configuration
    deleteProviderConfiguration: (providerId: number) =>
      api.delete(`/api/logistics/admin/providers/${providerId}`),

    // Get service statistics
    getServiceStatistics: () =>
      api.get('/api/logistics/admin/statistics'),

    // Get provider failure statistics
    getProviderFailureStats: () =>
      api.get('/api/logistics/admin/provider-failures'),

    // Reset provider failure counters
    resetProviderFailures: (provider: string) =>
      api.post(`/api/logistics/admin/provider-failures/${provider}/reset`),
  },
};

// User Management API (admin only)
export const userManagementAPI = {
  listUsers: () => api.get('/users/'),
  updateUser: (userId: number, data: { role?: string; is_active?: boolean }) =>
    api.put(`/users/${userId}`, data),
};

// Saved Views / Persistent Filters API
export interface SavedView {
  id: number;
  module: string;
  name: string;
  filters: Record<string, any>;
  is_default: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export const savedViewsAPI = {
  list: (module: string) =>
    api.get<SavedView[]>('/api/saved-views/', { params: { module } }),

  create: (data: { module: string; name: string; filters: Record<string, any>; is_default?: boolean }) =>
    api.post<SavedView>('/api/saved-views/', data),

  update: (
    id: number,
    data: { name?: string; filters?: Record<string, any>; is_default?: boolean },
  ) => api.put<SavedView>(`/api/saved-views/${id}`, data),

  remove: (id: number) => api.delete(`/api/saved-views/${id}`),
};
